"use client"

import Iconfont from "@repo/ui/iconfont"
import React, { useCallback, useEffect, useRef, useState } from "react"

import { MainNavigation, SearchAndSubscribe } from "../../../index"

import "../../index.scss"

import { useLocale } from "next-intl"

import { NavigationItem } from "../../../MainNavigation/type"
import Logo from "./Logo"

// SVG 图标组件
interface IconProps {
  className?: string
  onClick?: () => void
  style?: React.CSSProperties
  "aria-label"?: string
}

/**
 * 关闭图标组件
 */
const CloseIcon: React.FC<IconProps> = ({ className, onClick, style, "aria-label": ariaLabel }) => {
  const fontSize = style?.fontSize || 20
  const size = typeof fontSize === "number" ? `${fontSize}px` : fontSize

  return (
    <svg
      className={className}
      onClick={onClick}
      style={style}
      aria-label={ariaLabel}
      viewBox="0 0 1024 1024"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
    >
      <path
        d="M281.344 281.270857a36.571429 36.571429 0 0 1 51.712 0l409.6 409.6a36.571429 36.571429 0 0 1-51.675429 51.748572l-409.6-409.6a36.571429 36.571429 0 0 1 0-51.748572z"
        fill="currentColor"
      />
      <path
        d="M690.944 281.270857a36.571429 36.571429 0 0 1 51.748571 51.712l-409.6 409.6a36.571429 36.571429 0 0 1-51.748571-51.675428l409.6-409.6z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * 菜单图标组件（汉堡菜单）
 */
const MenuIcon: React.FC<IconProps> = ({ className, onClick, style, "aria-label": ariaLabel }) => {
  const fontSize = style?.fontSize || 20
  const size = typeof fontSize === "number" ? `${fontSize}px` : fontSize

  return (
    <svg
      className={className}
      onClick={onClick}
      style={style}
      aria-label={ariaLabel}
      viewBox="0 0 1024 1024"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
    >
      <path
        d="M852.224 218.9824a35.84 35.84 0 0 1 0 71.68H169.5744a35.84 35.84 0 0 1 0-71.68h682.6496zM852.224 474.9824a35.84 35.84 0 0 1 0 71.68H169.5744a35.84 35.84 0 0 1 0-71.68h682.6496zM852.224 730.9824a35.84 35.84 0 0 1 0 71.68H169.5744a35.84 35.84 0 0 1 0-71.68h682.6496z"
        fill="currentColor"
      />
    </svg>
  )
}

/**
 * 搜索图标组件
 */
const SearchIcon: React.FC<IconProps> = ({ className, onClick, style, "aria-label": ariaLabel }) => {
  const fontSize = style?.fontSize || 20
  const size = typeof fontSize === "number" ? `${fontSize}px` : fontSize

  return (
    <svg
      className={className}
      onClick={onClick}
      style={style}
      aria-label={ariaLabel}
      viewBox="0 0 1024 1024"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
    >
      <path
        d="M894.634667 852.693333L834.261333 913.066667l-157.525333-157.568 60.330667-60.330667 157.568 157.525333z"
        fill="currentColor"
      />
      <path
        d="M748.586667 488.704a259.84 259.84 0 1 0-519.765334 0 259.84 259.84 0 0 0 519.765334 0z m85.333333 0a345.173333 345.173333 0 1 1-690.432 0 345.173333 345.173333 0 0 1 690.432 0z"
        fill="currentColor"
      />
    </svg>
  )
}

interface MainNavigationBarProps {
  scrollState: "scrolled" | "transparent"
  logo: {
    href: string
    src: string
  }
  menuItems: NavigationItem[]
  onMobileMenuToggle: () => void
  onMobileSearchToggle: () => void
  isMobileMenuOpen: boolean
  isMobileSearchOpen: boolean
}

/**
 * 主导航栏组件（客户端组件，因为需要移动端按钮交互）
 */
export const MainNavigationBar: React.FC<MainNavigationBarProps> = ({
  scrollState,
  logo,
  menuItems,
  onMobileMenuToggle,
  onMobileSearchToggle,
  isMobileMenuOpen,
  isMobileSearchOpen,
}) => {
  const locale = useLocale()
  const navContainerRef = useRef<HTMLDivElement>(null)
  const navContentRef = useRef<HTMLElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)

  // 检查滚动状态
  const checkScrollability = useCallback(() => {
    if (!navContainerRef.current || !navContentRef.current) {
      setIsScrollable(false)
      setCanScrollLeft(false)
      setCanScrollRight(false)
      return
    }

    const containerWidth = navContainerRef.current.offsetWidth
    const contentWidth = navContentRef.current.scrollWidth
    const scrollLeft = navContentRef.current.scrollLeft

    const needsScroll = contentWidth > containerWidth
    setIsScrollable(needsScroll)

    if (needsScroll) {
      // 检查是否可以向左滚动
      const canScrollToLeft = scrollLeft > 1
      setCanScrollLeft(canScrollToLeft)

      // 检查是否可以向右滚动（允许 1px 的误差）
      const canScrollToRight = scrollLeft < contentWidth - containerWidth - 1
      setCanScrollRight(canScrollToRight)
    } else {
      setCanScrollLeft(false)
      setCanScrollRight(false)
    }
  }, [])

  // 处理向左滚动（滚动到最左侧）
  const handleScrollLeft = useCallback(() => {
    if (!navContentRef.current) return

    // 直接滚动到最左侧
    navContentRef.current.scrollTo({
      left: 0,
      behavior: "smooth",
    })
  }, [])

  // 处理向右滚动（滚动到最右侧）
  const handleScrollRight = useCallback(() => {
    if (!navContentRef.current) return

    // 计算最大滚动距离
    const maxScroll = navContentRef.current.scrollWidth - navContentRef.current.offsetWidth

    // 直接滚动到最右侧
    navContentRef.current.scrollTo({
      left: maxScroll,
      behavior: "smooth",
    })
  }, [])

  // 监听滚动事件
  useEffect(() => {
    const navElement = navContentRef.current
    if (!navElement) return

    const handleScroll = () => {
      checkScrollability()
    }

    navElement.addEventListener("scroll", handleScroll)
    return () => {
      navElement.removeEventListener("scroll", handleScroll)
    }
  }, [checkScrollability])

  // 禁用滚动功能（禁用鼠标滚轮，只能通过按钮滚动）
  useEffect(() => {
    const navElement = navContentRef.current
    if (!navElement) return

    // 禁用鼠标滚轮横向滚动
    const handleWheel = (e: WheelEvent) => {
      // 阻止横向滚动
      if (e.deltaX !== 0) {
        e.preventDefault()
      }
    }

    navElement.addEventListener("wheel", handleWheel, { passive: false })

    return () => {
      navElement.removeEventListener("wheel", handleWheel)
    }
  }, [])

  // 监听窗口尺寸变化和容器尺寸变化
  useEffect(() => {
    // 使用 ResizeObserver 监听容器尺寸变化
    const containerObserver = new ResizeObserver(() => {
      checkScrollability()
    })

    const contentObserver = new ResizeObserver(() => {
      checkScrollability()
    })

    if (navContainerRef.current) {
      containerObserver.observe(navContainerRef.current)
    }
    if (navContentRef.current) {
      contentObserver.observe(navContentRef.current)
    }

    // 监听窗口 resize
    const handleResize = () => {
      checkScrollability()
    }
    window.addEventListener("resize", handleResize)

    // 初始检查
    checkScrollability()

    return () => {
      containerObserver.disconnect()
      contentObserver.disconnect()
      window.removeEventListener("resize", handleResize)
    }
  }, [checkScrollability, menuItems])

  return (
    <div
      className={`mainNavigationBar ${scrollState}`}
      style={
        isMobileSearchOpen
          ? {
              borderBottom: "none",
            }
          : {}
      }
    >
      <div className="container">
        <div className="mainNavigationContent">
          {/* Logo 和主导航 */}
          <div className="logoAndNavSection">
            <Logo logo={logo} locale={locale} />
            {/* 主导航菜单滚动容器 */}
            <div className="mainNavigationWrapper" ref={navContainerRef}>
              {/* 左侧滚动按钮 */}
              {isScrollable && canScrollLeft && (
                <div className="scrollButton scrollButtonLeft" onClick={handleScrollLeft} aria-label="向左滚动">
                  <Iconfont
                    type="icon-daohangjiantou"
                    style={{ color: "#fff", fontSize: 14, transform: "rotate(180deg)" }}
                  />
                </div>
              )}
              <MainNavigation items={menuItems} ref={navContentRef} />
              {/* 右侧滚动按钮 */}
              {isScrollable && canScrollRight && (
                <div className="scrollButton scrollButtonRight" onClick={handleScrollRight} aria-label="向右滚动">
                  <Iconfont type="icon-daohangjiantou" style={{ color: "#fff", fontSize: 14 }} />
                </div>
              )}
            </div>
          </div>
          {/* 右侧搜索和订阅 */}
          <SearchAndSubscribe />
          {/* 移动端汉堡菜单和搜索图标 */}
          <div className="mobileControlButtons">
            {isMobileMenuOpen ? (
              <CloseIcon
                className="mobileMenuToggleButton"
                onClick={onMobileMenuToggle}
                style={{ color: "#0c1019", fontSize: 24, cursor: "pointer" }}
              />
            ) : (
              <MenuIcon
                className="mobileMenuToggleButton"
                onClick={onMobileMenuToggle}
                style={{ color: "#0c1019", fontSize: 20, cursor: "pointer" }}
              />
            )}
            {isMobileSearchOpen ? (
              <CloseIcon
                className="mobileMenuToggleButton"
                onClick={onMobileSearchToggle}
                style={{ color: "#0c1019", fontSize: 20, cursor: "pointer" }}
              />
            ) : (
              <SearchIcon
                className="mobileSearchIconButton"
                onClick={onMobileSearchToggle}
                aria-label="search"
                style={{ color: "#0c1019", fontSize: 20, cursor: "pointer" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
